import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, FileUp, ArrowLeft, Trash2, FileText, CheckCheck, FolderPlus, RefreshCcw } from 'lucide-react';
import { ImportItemRow } from './ImportItemRow';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = '.docx,.pdf,.txt,.md,.xls,.xlsx';
const STEPS = ['Upload', 'Recommend Categories', 'Approve Categories', 'Parse Questions', 'Review Capabilities'];
const BUSY_STATUSES = ['analyzing', 'parsing'];
const STALL_MS = 4 * 60 * 1000;

const isStalled = (s) => {
  if (!BUSY_STATUSES.includes(s.status)) return false;
  const ref = s.progress?.updated_at || s.created_at;
  if (!ref) return false;
  return Date.now() - new Date(ref).getTime() > STALL_MS;
};

export const fmtEta = (secs) => {
  if (secs == null || secs <= 0) return '';
  if (secs < 60) return ` · ~${Math.max(5, Math.round(secs / 5) * 5)}s left`;
  return ` · ~${Math.ceil(secs / 60)}m left`;
};

const stepFor = (status) => {
  if (status === 'analyzing') return 2;
  if (status === 'categories_ready') return 3;
  if (status === 'parsing') return 4;
  return 5;
};

const StageBar = ({ current }) => (
  <div className="flex items-center gap-1" data-testid="rfp-import-stagebar">
    {STEPS.map((label, i) => {
      const n = i + 1;
      const state = n < current ? 'done' : n === current ? 'active' : 'todo';
      return (
        <React.Fragment key={label}>
          {i > 0 && <div className={`h-px flex-1 ${n <= current ? 'bg-[#4ABFCF]' : 'bg-slate-200'}`} />}
          <div className="flex items-center gap-1 shrink-0" title={label}>
            <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-semibold
              ${state === 'done' ? 'bg-[#4ABFCF] text-white' : state === 'active' ? 'bg-[#0F2040] text-white' : 'bg-slate-100 text-slate-400'}`}>{n}</span>
            <span className={`text-[9px] hidden sm:inline ${state === 'active' ? 'text-[#0F2040] font-semibold' : 'text-slate-400'}`}>{label}</span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

const ProgressPanel = ({ session }) => {
  const p = session?.progress || {};
  return (
    <div className="flex flex-col items-center gap-3 py-8" data-testid="rfp-import-progress">
      <Loader2 className="w-7 h-7 animate-spin text-[#4ABFCF]" />
      <p className="text-sm text-slate-600" data-testid="rfp-import-progress-label">{p.label || 'Working…'}{fmtEta(p.eta_seconds)}</p>
      <div className="w-full max-w-sm h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#4ABFCF] rounded-full transition-all duration-700"
          style={{ width: `${Math.max(4, p.pct || 0)}%` }} data-testid="rfp-import-progress-bar" />
      </div>
      <p className="text-[10px] text-slate-400">You can close this — the import keeps running and appears under "Previous imports".</p>
    </div>
  );
};

const CategoryApproval = ({ session, onContinue, busy }) => {
  const proposed = (session.recommended_categories || []).filter(r => r.status === 'proposed');
  const [rows, setRows] = useState(() => proposed.map(r => ({ ...r, approve: true })));
  const edit = (idx, field, val) => setRows(prev => prev.map(r => (r.idx === idx ? { ...r, [field]: val } : r)));
  return (
    <div className="space-y-3" data-testid="rfp-import-categories-stage">
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center" data-testid="rfp-import-nocats">
          The existing categories already cover this document's question intent — no new categories recommended.
        </p>
      ) : (
        <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
          {rows.map(r => (
            <div key={r.idx} className="px-3 py-2.5 flex items-start gap-3" data-testid={`rfp-import-cat-${r.idx}`}>
              <Checkbox checked={r.approve} onCheckedChange={v => edit(r.idx, 'approve', !!v)}
                className="mt-1.5" data-testid={`rfp-import-cat-approve-${r.idx}`} />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex gap-2">
                  <Input value={r.name} onChange={e => edit(r.idx, 'name', e.target.value)}
                    className="h-7 text-xs flex-1" data-testid={`rfp-import-cat-name-${r.idx}`} />
                  <Input value={r.shortname} maxLength={6}
                    onChange={e => edit(r.idx, 'shortname', e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                    className="h-7 text-xs w-20 font-mono uppercase" data-testid={`rfp-import-cat-short-${r.idx}`} />
                </div>
                {r.reason && <p className="text-[10px] text-slate-500 leading-relaxed">{r.reason}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end">
        <Button size="sm" onClick={() => onContinue(rows)} disabled={busy}
          className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-import-approve-cats-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5 mr-1" />}
          {rows.some(r => r.approve) ? 'Create approved categories & parse questions' : 'Continue to parse questions'}
        </Button>
      </div>
    </div>
  );
};

export const ImportWizardDialog = ({ open, onOpenChange, categories, onLibraryChanged }) => {
  const [stage, setStage] = useState('home'); // home | working | categories | review
  const [sessions, setSessions] = useState(null);
  const [session, setSession] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [catBusy, setCatBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkCat, setBulkCat] = useState('');
  const [bulkScope, setBulkScope] = useState('uncategorised');
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/import`, { withCredentials: true });
      setSessions(res.data?.sessions || []);
    } catch { setSessions([]); }
  }, []);

  const resumeParse = async (s) => {
    try {
      const res = await axios.post(`${API_URL}/api/rfp/import/${s.id}/resume`, {}, { withCredentials: true });
      toast.success(res.data?.remaining != null
        ? `Resuming — ${res.data.remaining} section${res.data.remaining === 1 ? '' : 's'} left`
        : 'Resuming category analysis…');
      loadSessions();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not resume the import'); }
  };

  useEffect(() => {
    if (open) { setStage('home'); setSession(null); loadSessions(); }
    return () => clearInterval(pollRef.current);
  }, [open, loadSessions]);

  const applyStatus = useCallback((data) => {
    setSession(data);
    if (BUSY_STATUSES.includes(data.status)) setStage('working');
    else if (data.status === 'categories_ready') setStage('categories');
    else setStage('review');
  }, []);

  const openSession = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/import/${id}`, { withCredentials: true });
      applyStatus(res.data);
      clearInterval(pollRef.current);
      if (BUSY_STATUSES.includes(res.data.status)) {
        pollRef.current = setInterval(async () => {
          try {
            const r = await axios.get(`${API_URL}/api/rfp/import/${id}`, { withCredentials: true });
            applyStatus(r.data);
            if (!BUSY_STATUSES.includes(r.data.status)) clearInterval(pollRef.current);
          } catch { /* transient edge/network error — keep polling */ }
        }, 2500);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open the import session');
    }
  }, [applyStatus]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/rfp/import`, fd, { withCredentials: true });
      toast.success(`"${file.name}" uploaded — analysing question intent`);
      openSession(res.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const approveCategories = async (rows) => {
    setCatBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/import/${session.id}/approve-categories`,
        { categories: rows.map(r => ({ idx: r.idx, approve: r.approve, name: r.name, shortname: r.shortname })) },
        { withCredentials: true });
      const created = res.data?.created || [];
      if (created.length) toast.success(`${created.length} categor${created.length === 1 ? 'y' : 'ies'} created — parsing questions`);
      onLibraryChanged();
      openSession(session.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not approve categories');
    } finally { setCatBusy(false); }
  };

  const removeSession = async (s) => {
    if (!window.confirm(`Delete import "${s.filename}"? Already-accepted capabilities stay in the library.`)) return;
    try {
      await axios.delete(`${API_URL}/api/rfp/import/${s.id}`, { withCredentials: true });
      loadSessions();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete'); }
  };

  const refreshSession = useCallback(async () => {
    if (!session) return;
    const r = await axios.get(`${API_URL}/api/rfp/import/${session.id}`, { withCredentials: true });
    setSession(r.data);
    onLibraryChanged();
  }, [session, onLibraryChanged]);

  const counts = session?.counts || {};
  const matchedPending = (session?.items || []).filter(i => i.status === 'pending' && (i.category_ids || []).length > 0).length;

  const acceptAllMatched = async () => {
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/import/${session.id}/accept-all-matched`, {}, { withCredentials: true });
      const { accepted, errors } = res.data || {};
      const flagged = (errors || []).filter(e => /sensitive/i.test(e.detail || '')).length;
      toast.success(`${accepted} capabilit${accepted === 1 ? 'y' : 'ies'} accepted into the library${flagged ? ` · ${flagged} need${flagged === 1 ? 's' : ''} review (client names / sensitive terms)` : ''}${(errors?.length || 0) - flagged ? ` · ${errors.length - flagged} failed` : ''}`);
      refreshSession();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk accept failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkAssign = async () => {
    if (!bulkCat) { toast.error('Pick a category first'); return; }
    const targets = (session.items || []).filter(i => i.status === 'pending'
      && (bulkScope === 'all' || (i.category_ids || []).length === 0));
    if (!targets.length) { toast.info('No matching pending items'); return; }
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/import/${session.id}/bulk-categorize`,
        { idxs: targets.map(i => i.idx), category_ids: [bulkCat] }, { withCredentials: true });
      toast.success(`Category applied to ${res.data.updated} item${res.data.updated === 1 ? '' : 's'}`);
      refreshSession();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk assign failed');
    } finally { setBulkBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rfp-import-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="w-4 h-4 text-[#4ABFCF]" />
            {stage === 'review' ? `Review — ${session?.filename}`
              : stage === 'categories' ? `Approve categories — ${session?.filename}`
              : 'Import Capabilities from a Document'}
          </DialogTitle>
          <DialogDescription>
            {stage === 'home' && 'Upload an existing RFP response — the 5-stage import recommends categories from question intent, then parses capabilities for your review.'}
            {stage === 'working' && 'The AI is working through the document — progress below.'}
            {stage === 'categories' && 'Review the recommended categories (based on the intent of the document\u2019s questions). Approve, rename or untick them, then continue to parsing.'}
            {stage === 'review' && 'Each capability stays "pending review" until you accept it (with any edits) or skip it. Nothing touches the library until accepted.'}
          </DialogDescription>
        </DialogHeader>

        {stage !== 'home' && session && <StageBar current={stepFor(session.status)} />}

        {stage === 'home' && (
          <div className="space-y-4">
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl py-8 cursor-pointer hover:border-[#4ABFCF] hover:bg-slate-50 transition-colors"
              data-testid="rfp-import-dropzone">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" /> : <FileUp className="w-6 h-6 text-slate-400" />}
              <span className="text-sm font-medium text-[#0F2040]">{uploading ? 'Uploading…' : 'Choose a document'}</span>
              <span className="text-[10px] text-slate-400">.docx · .pdf · .txt · .md · .xls · .xlsx — max 15 MB</span>
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" disabled={uploading}
                onChange={e => upload(e.target.files?.[0])} data-testid="rfp-import-file-input" />
            </label>

            {Array.isArray(sessions) && sessions.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#0F2040] mb-2">Previous imports — resume reviewing</p>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-52 overflow-y-auto">
                  {sessions.map(s => (
                    <div key={s.id} className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-slate-50" data-testid={`rfp-import-session-${s.id}`}>
                      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <button onClick={() => openSession(s.id)} className="font-medium text-[#0F2040] hover:underline truncate flex-1 text-left"
                        data-testid={`rfp-import-resume-${s.id}`}>{s.filename}</button>
                      <span className="text-[9px] text-slate-400 shrink-0">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
                      {BUSY_STATUSES.includes(s.status) ? (isStalled(s) ? (
                        <>
                          <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200"
                            title="No progress for over 4 minutes — likely interrupted by a restart or a hung AI call"
                            data-testid={`rfp-import-stalled-${s.id}`}>
                            Stalled{s.progress?.pct != null ? ` at ${s.progress.pct}%` : ''}
                          </Badge>
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 shrink-0"
                            onClick={() => resumeParse(s)} data-testid={`rfp-import-resume-parse-${s.id}`}>
                            <RefreshCcw className="w-3 h-3 mr-1" />Resume
                          </Button>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">
                          {s.progress?.label || 'Working…'} {s.progress?.pct != null ? `${s.progress.pct}%` : ''}{fmtEta(s.progress?.eta_seconds)}
                        </Badge>
                      )) : s.status === 'categories_ready' ? (
                        <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">Awaiting category approval</Badge>
                      ) : s.status === 'failed' ? (
                        <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">Failed</Badge>
                      ) : (
                        <span className="text-[9px] text-slate-500 shrink-0">
                          {s.counts.pending} pending · {s.counts.confirmed} accepted · {s.counts.skipped} skipped
                        </span>
                      )}
                      <button onClick={() => removeSession(s)} className="text-slate-300 hover:text-red-600"
                        data-testid={`rfp-import-delete-${s.id}`} title="Delete import session"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {stage === 'working' && <ProgressPanel session={session} />}

        {stage === 'categories' && session && (
          <CategoryApproval key={session.id} session={session} onContinue={approveCategories} busy={catBusy} />
        )}

        {stage === 'review' && session && (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5" data-testid="rfp-import-note-row">
              <Input defaultValue={session.note || ''} key={`note-${session.id}-${session.note || ''}`}
                placeholder="Session note — source and purpose (e.g. 'UPS master responses, for EU bids')"
                className="h-7 text-[11px] flex-1" id={`rfp-note-${session.id}`}
                data-testid="rfp-import-note-input" />
              <Button size="sm" variant="outline" className="h-7 text-[10px] px-2" data-testid="rfp-import-note-save"
                onClick={async () => {
                  const v = document.getElementById(`rfp-note-${session.id}`)?.value || '';
                  try {
                    await axios.put(`${API_URL}/api/rfp/import/${session.id}/note`, { note: v }, { withCredentials: true });
                    toast.success('Note saved');
                  } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save the note'); }
                }}>Save note</Button>
            </div>
            {session.failed_sections > 0 && (
              <div className="flex items-center gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
                data-testid="rfp-import-failed-notice">
                <span className="flex-1">
                  {session.failed_sections} section{session.failed_sections === 1 ? '' : 's'} couldn't be parsed — capabilities from {session.failed_sections === 1 ? 'it are' : 'them are'} missing from this list.
                </span>
                <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                  data-testid="rfp-import-retry-failed"
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/api/rfp/import/${session.id}/retry-failed`, {}, { withCredentials: true });
                      toast.success('Retrying the failed sections — new capabilities will be added to this list');
                      openSession(session.id);
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not retry'); }
                  }}>
                  Retry failed sections
                </Button>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => { setSession(null); setStage('home'); loadSessions(); }}
                data-testid="rfp-import-back-btn">
                <ArrowLeft className="w-3 h-3 mr-1" />All imports
              </Button>
              <span className="flex-1" />
              {matchedPending > 0 && (
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={acceptAllMatched} disabled={bulkBusy} data-testid="rfp-import-accept-all-btn"
                  title="Accept every pending capability that already has a matched category — unmatched ones stay for manual review">
                  {bulkBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCheck className="w-3 h-3 mr-1" />}
                  Accept all matched ({matchedPending})
                </Button>
              )}
              <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid="rfp-import-pending-count">{counts.pending || 0} pending</Badge>
              {(counts.flagged || 0) > 0 && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-800 border-amber-300" data-testid="rfp-import-flagged-count" title="Items containing client names, pricing figures or contact details — resolve before accepting">{counts.flagged} need{counts.flagged === 1 ? 's' : ''} scrub review</Badge>}
              <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">{counts.confirmed || 0} accepted</Badge>
              <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500 border-slate-200">{counts.skipped || 0} skipped</Badge>
            </div>
            {session.status === 'failed' ? (
              <p className="text-sm text-rose-600 py-6 text-center">Parsing failed: {session.error || 'unknown error'}</p>
            ) : session.status === 'empty' || (session.items || []).length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="rfp-import-empty">No reusable capabilities were found in this document.</p>
            ) : (
              <>
                {(counts.pending || 0) > 1 && (
                  <div className="flex items-center gap-2 flex-wrap border border-slate-200 bg-slate-50 rounded-md px-3 py-2" data-testid="rfp-import-bulk-bar">
                    <span className="text-[11px] font-semibold text-[#0F2040]">Bulk category:</span>
                    <select value={bulkScope} onChange={e => setBulkScope(e.target.value)}
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-700" data-testid="rfp-import-bulk-scope">
                      <option value="uncategorised">Uncategorised pending only</option>
                      <option value="all">All pending items</option>
                    </select>
                    <select value={bulkCat} onChange={e => setBulkCat(e.target.value)}
                      className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-700 min-w-[160px]" data-testid="rfp-import-bulk-cat">
                      <option value="">Choose category…</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <Button size="sm" onClick={bulkAssign} disabled={bulkBusy || !bulkCat}
                      className="h-7 text-[11px] bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-import-bulk-apply">
                      {bulkBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}Apply
                    </Button>
                    <span className="text-[10px] text-slate-400">Speeds up big imports — set once, then fine-tune per item</span>
                  </div>
                )}
                <div className="space-y-2" data-testid="rfp-import-items">
                  {session.items.map(item => (
                    <ImportItemRow key={item.idx} sessionId={session.id} item={item}
                      categories={categories} onItemChanged={refreshSession} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
